/**
 * Phase 21β-2.8 — Dialog "Mietvertrag aus Vorlage generieren"
 *
 * Used inside TenancyDetail to spawn a new contract PDF/DOCX from a saved
 * Vertragsvorlage.  Optional blocks can be toggled before rendering.
 */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API, formatApiError, getToken } from "@/lib/api";
import {
  FileText, CheckCircle, Circle, FilePdf, FileDoc, Briefcase, Spinner,
} from "@phosphor-icons/react";

export default function GenerateContractDialog({ open, onOpenChange, tenancyId, propertyId, contactId, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [fullTpl, setFullTpl] = useState(null);
  const [enabledIds, setEnabledIds] = useState(null);  // Set of block ids to include
  const [docTitle, setDocTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveToDocs, setSaveToDocs] = useState(true);

  // Load templates on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/contract-templates");
        const active = (data || []).filter((t) => !t.is_archived);
        setTemplates(active);
        // pick default
        const def = active.find((t) => t.is_default) || active[0];
        if (def) setSelectedId(def.id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open]);

  // Load full template (blocks) when selection changes
  useEffect(() => {
    if (!selectedId) { setFullTpl(null); setEnabledIds(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/contract-templates/${selectedId}`);
        setFullTpl(data);
        // Initial enabled set = all blocks where (!optional || enabled===true)
        const init = new Set(
          (data.blocks || [])
            .filter((b) => !b.optional || b.enabled !== false)
            .map((b) => b.id)
        );
        setEnabledIds(init);
        setDocTitle(`${data.name} – ${new Date().toLocaleDateString("de-DE")}`);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [selectedId]);

  const toggleBlock = (id) => {
    setEnabledIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const enabledCount = enabledIds ? enabledIds.size : 0;
  const totalCount = fullTpl?.blocks?.length || 0;

  const generate = async (format) => {
    if (!fullTpl || !enabledIds) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API}/contract-templates/${fullTpl.id}/render?format=${format}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tenancy_id: tenancyId,
            property_id: propertyId,
            contact_id: contactId,
            enabled_block_ids: Array.from(enabledIds),
            save_to_documents: saveToDocs,
            document_title: docTitle,
          }),
        }
      );
      if (!res.ok) {
        const t = await res.text();
        toast.error(`Erzeugung fehlgeschlagen: ${t}`);
        return;
      }
      // Download client-side
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ext = format === "pdf" ? "pdf" : "docx";
      a.download = `${docTitle || "Vertrag"}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);
      if (saveToDocs) {
        toast.success("Vertrag erstellt und in der Akte abgelegt");
      } else {
        toast.success("Vertrag erstellt");
      }
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="generate-contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase size={18} className="text-emerald-600" />
            Mietvertrag aus Vorlage generieren
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-[260px_1fr] gap-4 min-h-[420px]">
          {/* Left: template selector */}
          <div className="border-r border-slate-200 pr-3 space-y-1 overflow-y-auto max-h-[460px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Vertragsvorlagen
            </div>
            {templates.length === 0 && (
              <div className="text-xs text-slate-400 italic">
                Keine aktiven Vertragsvorlagen.
              </div>
            )}
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-2 py-1.5 rounded-sm text-xs border ${
                  selectedId === t.id
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "border-transparent hover:bg-slate-50 text-slate-700"
                }`}
                data-testid={`gc-template-${t.id}`}
              >
                <div className="flex items-center gap-1.5">
                  <FileText size={12} className="shrink-0" />
                  <span className="truncate flex-1 font-medium">{t.name}</span>
                  {t.is_default && (
                    <span className="text-[9px] uppercase text-emerald-600">std</span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {t.contract_type} · {t.enabled_count || 0}/{t.block_count || 0} aktiv
                </div>
              </button>
            ))}
          </div>

          {/* Right: blocks toggles + meta */}
          <div className="space-y-2 overflow-y-auto max-h-[460px]">
            {!fullTpl ? (
              <div className="text-sm text-slate-400 italic">
                Wähle eine Vertragsvorlage links.
              </div>
            ) : (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    Dokumenttitel
                  </label>
                  <Input
                    value={docTitle}
                    onChange={(e) => setDocTitle(e.target.value)}
                    className="h-8 text-sm"
                    data-testid="gc-doc-title"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                    <span>Bausteine ({enabledCount}/{totalCount} aktiv)</span>
                  </div>
                  <ul className="space-y-0.5">
                    {(fullTpl.blocks || []).map((b) => {
                      const enabled = enabledIds?.has(b.id);
                      return (
                        <li key={b.id}>
                          <button
                            onClick={() => toggleBlock(b.id)}
                            data-testid={`gc-block-${b.id}`}
                            className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-sm border ${
                              enabled
                                ? "border-emerald-200 bg-emerald-50/40"
                                : "border-dashed border-slate-200 bg-slate-50/40 text-slate-500"
                            }`}
                          >
                            {enabled ? (
                              <CheckCircle weight="fill" size={14} className="text-emerald-500 shrink-0" />
                            ) : (
                              <Circle size={14} className="text-slate-300 shrink-0" />
                            )}
                            <span className="text-xs font-medium flex-1 truncate">
                              {b.title || "Ohne Titel"}
                            </span>
                            {b.optional && (
                              <span className="text-[9px] uppercase text-amber-700 bg-amber-50 px-1 rounded-sm">
                                optional
                              </span>
                            )}
                            {b.category && (
                              <span className="text-[9px] text-slate-500 hidden md:inline">{b.category}</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-slate-700 pt-2">
                  <input
                    type="checkbox" checked={saveToDocs}
                    onChange={(e) => setSaveToDocs(e.target.checked)}
                    data-testid="gc-save-to-docs"
                  />
                  In der Mietverhältnis-Akte ablegen (Kategorie „Verträge")
                </label>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={busy} data-testid="gc-cancel">
            Abbrechen
          </Button>
          <Button
            variant="outline"
            disabled={busy || !fullTpl || enabledCount === 0}
            onClick={() => generate("docx")}
            data-testid="gc-generate-docx"
            className="gap-1.5"
          >
            <FileDoc size={14} /> DOCX
          </Button>
          <Button
            disabled={busy || !fullTpl || enabledCount === 0}
            onClick={() => generate("pdf")}
            className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
            data-testid="gc-generate-pdf"
          >
            {busy ? <Spinner size={14} className="animate-spin" /> : <FilePdf size={14} />}
            PDF erzeugen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
